import { useState, useEffect, useCallback, useMemo } from "react";
import { experiment } from "@amplitude/unified";
import { getSessionToken } from "../lib/sessionToken";
import { FEATURE_GATES_REFRESHED_EVENT } from "../lib/analytics";
export function useFeatureGate(featureKey) {
  const [variant, setVariant] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const tokenSnapshot = getSessionToken() || "anonymous";

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    function applyVariant() {
      if (cancelled) return;
      const client = experiment();
      if (client) {
        const v = client.variant(featureKey);
        setVariant(v ?? undefined);
      }
      setIsLoading(false);
    }

    async function runFetch() {
      const client = experiment();
      if (!client) {
        setVariant(undefined);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setVariant(undefined);

      try {
        await client.fetch();
      } catch (err) {
        console.error("[FeatureGate] fetch failed:", err);
      } finally {
        if (cancelled) return;
        applyVariant();
        // Re-read after a tick; SDK may apply fetch results asynchronously.
        timeoutId = setTimeout(applyVariant, 0);
      }
    }

    runFetch();
    // identifyUser fetches the flags again for the identified user; re-read
    // this one in place, without going back to loading, so gated UI doesn't
    // blink.
    window.addEventListener(FEATURE_GATES_REFRESHED_EVENT, applyVariant);

    return () => {
      cancelled = true;
      window.removeEventListener(FEATURE_GATES_REFRESHED_EVENT, applyVariant);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [featureKey, tokenSnapshot]);

  const getVariant = useCallback(() => variant, [variant]);

  const hasFeature = useCallback((variantName = "on") => variant?.value === variantName, [variant]);

  return useMemo(
    () => ({
      getVariant,
      hasFeature,
      value: variant?.value,
      payload: variant?.payload,
      isLoading,
    }),
    [getVariant, hasFeature, variant, isLoading]
  );
}
